import {
  Container,
  Title,
  Text,
  Paper,
  TextInput,
  Textarea,
  NumberInput,
  Button,
  Group,
  Grid,
  Stack,
  Loader,
  Center,
  MultiSelect,
  FileInput,
  Badge,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
  IconCheck,
  IconAlertCircle,
  IconUpload,
  IconFileText,
  IconTrash,
} from '@tabler/icons-react';
import {
  useDeleteOwnHealthCertificate,
  useUpdateUserProfile,
  useUploadOwnHealthCertificate,
} from '../../hooks/useVolunteer';
import { useAuth } from '../../hooks/useAuth';
import { useVolunteer } from '../../hooks/useVolunteer';
import { userApi } from '../../api/volunteer';
import type {
  UpdateProfileDto,
  Department,
  Weekday,
} from '../../api/volunteer';
import { BackLink } from '@/components/BackLink';

interface ProfileFormData extends UpdateProfileDto {
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  zipCode?: string;
  city?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelation?: string;
  motivation?: string;
  experience?: string;
  availability?: string;
  department?: Department[];
  workingHoursPerMonth?: number;
  weekdaysAvailable?: Weekday[];
  languages?: string;
}

const DEPARTMENTS = [
  { value: 'DISTRIBUTION', label: 'distribution' },
  { value: 'COOKING', label: 'cooking' },
  { value: 'LOGISTICS', label: 'logistics' },
  { value: 'STOREFRONT', label: 'storefront' },
];

const WEEKDAYS = [
  { value: 'MONDAY', label: 'monday' },
  { value: 'TUESDAY', label: 'tuesday' },
  { value: 'WEDNESDAY', label: 'wednesday' },
  { value: 'THURSDAY', label: 'thursday' },
  { value: 'FRIDAY', label: 'friday' },
  { value: 'SATURDAY', label: 'saturday' },
  { value: 'SUNDAY', label: 'sunday' },
];

export default function ProfileSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: volunteer, isLoading: volunteerLoading } = useVolunteer(
    user?.volunteerId || ''
  );
  const updateProfileMutation = useUpdateUserProfile();
  const uploadHealthCertificateMutation = useUploadOwnHealthCertificate();
  const deleteHealthCertificateMutation = useDeleteOwnHealthCertificate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ProfileFormData>({
    initialValues: {
      firstName: '',
      lastName: '',
      phone: '',
      address: '',
      zipCode: '',
      city: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      emergencyContactRelation: '',
      motivation: '',
      experience: '',
      availability: '',
      department: [],
      workingHoursPerMonth: 0,
      weekdaysAvailable: [],
      languages: '',
    },
  });

  useEffect(() => {
    if (!volunteer) return;

    form.setValues({
      firstName: volunteer.firstName ?? '',
      lastName: volunteer.lastName ?? '',
      phone: volunteer.phone ?? '',
      address: volunteer.address ?? '',
      zipCode: volunteer.zipCode ?? '',
      city: volunteer.city ?? '',
      emergencyContactName: volunteer.emergencyContactName ?? '',
      emergencyContactPhone: volunteer.emergencyContactPhone ?? '',
      emergencyContactRelation: volunteer.emergencyContactRelation ?? '',
      motivation: volunteer.motivation ?? '',
      experience: volunteer.experience ?? '',
      availability: volunteer.availability ?? '',
      department: volunteer.department ?? [],
      workingHoursPerMonth: volunteer.workingHoursPerMonth ?? 0,
      weekdaysAvailable: volunteer.weekdaysAvailable ?? [],
      languages: volunteer.languages ?? '',
    });
  }, [volunteer]);

  const handleSubmit = async (values: ProfileFormData) => {
    setIsSubmitting(true);
    try {
      await updateProfileMutation.mutateAsync(values);
      notifications.show({
        title: t('profile.notifications.success'),
        message: t('profile.notifications.successMessage'),
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      // Refresh volunteer data
      window.location.reload();
    } catch (error) {
      notifications.show({
        title: t('profile.notifications.error'),
        message: t('profile.notifications.errorMessage'),
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHealthCertificateUpload = async (file: File | null) => {
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      notifications.show({
        title: t('profile.notifications.documentError'),
        message: t('profile.documents.invalidType'),
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      notifications.show({
        title: t('profile.notifications.documentError'),
        message: t('profile.documents.fileTooLarge'),
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
      return;
    }

    try {
      await uploadHealthCertificateMutation.mutateAsync(file);
      notifications.show({
        title: t('profile.notifications.documentUploaded'),
        message: t('profile.notifications.documentUploadedMessage'),
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch {
      notifications.show({
        title: t('profile.notifications.documentError'),
        message: t('profile.notifications.documentErrorMessage'),
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  const handleHealthCertificateDelete = async () => {
    try {
      await deleteHealthCertificateMutation.mutateAsync();
      notifications.show({
        title: t('profile.notifications.documentDeleted'),
        message: t('profile.notifications.documentDeletedMessage'),
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch {
      notifications.show({
        title: t('profile.notifications.documentError'),
        message: t('profile.notifications.documentErrorMessage'),
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  if (volunteerLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!volunteer) {
    return (
      <Container size="lg" py="xl">
        <Text c="red">{t('common.error')}</Text>
      </Container>
    );
  }

  const departmentOptions = DEPARTMENTS.map(dept => ({
    value: dept.value,
    label: t(`departments.${dept.label}`),
  }));

  const weekdayOptions = WEEKDAYS.map(day => ({
    value: day.value,
    label: t(`weekdays.${day.label}`),
  }));

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <div>
          <BackLink to="/app" />
          <Title order={1} mb="xs">
            {t('profile.title')}
          </Title>
          <Text c="dimmed" size="sm">
            {t('profile.subtitle')}
          </Text>
        </div>

        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="lg">
              {/* Personal Information Section */}
              <div>
                <Title order={3} mb="md" size="h4">
                  {t('profile.sections.personal')}
                </Title>
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label={t('profile.fields.firstName')}
                      placeholder={t('profile.placeholders.firstName')}
                      {...form.getInputProps('firstName')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label={t('profile.fields.lastName')}
                      placeholder={t('profile.placeholders.lastName')}
                      {...form.getInputProps('lastName')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label={t('profile.fields.phone')}
                      placeholder={t('profile.placeholders.phone')}
                      {...form.getInputProps('phone')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label={t('profile.fields.address')}
                      placeholder={t('profile.placeholders.address')}
                      {...form.getInputProps('address')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label={t('profile.fields.zipCode')}
                      placeholder={t('profile.placeholders.zipCode')}
                      {...form.getInputProps('zipCode')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label={t('profile.fields.city')}
                      placeholder={t('profile.placeholders.city')}
                      {...form.getInputProps('city')}
                    />
                  </Grid.Col>
                </Grid>
              </div>

              {/* Emergency Contact Section */}
              <div>
                <Title order={3} mb="md" size="h4">
                  {t('profile.sections.emergency')}
                </Title>
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label={t('profile.fields.emergencyContactName')}
                      {...form.getInputProps('emergencyContactName')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label={t('profile.fields.emergencyContactPhone')}
                      {...form.getInputProps('emergencyContactPhone')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12 }}>
                    <TextInput
                      label={t('profile.fields.emergencyContactRelation')}
                      {...form.getInputProps('emergencyContactRelation')}
                    />
                  </Grid.Col>
                </Grid>
              </div>

              {/* Volunteer Information Section */}
              <div>
                <Title order={3} mb="md" size="h4">
                  {t('profile.sections.volunteer')}
                </Title>
                <Grid>
                  <Grid.Col span={{ base: 12 }}>
                    <Textarea
                      label={t('profile.fields.motivation')}
                      placeholder={t('profile.placeholders.motivation')}
                      minRows={3}
                      {...form.getInputProps('motivation')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12 }}>
                    <Textarea
                      label={t('profile.fields.experience')}
                      placeholder={t('profile.placeholders.experience')}
                      minRows={3}
                      {...form.getInputProps('experience')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12 }}>
                    <Textarea
                      label={t('profile.fields.availability')}
                      placeholder={t('profile.placeholders.availability')}
                      minRows={2}
                      {...form.getInputProps('availability')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12 }}>
                    <MultiSelect
                      label={t('profile.fields.department')}
                      placeholder={t('profile.fields.department')}
                      data={departmentOptions}
                      value={form.values.department ?? []}
                      onChange={value =>
                        form.setFieldValue('department', value as Department[])
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <NumberInput
                      label={t('profile.fields.workingHoursPerMonth')}
                      placeholder="0"
                      min={0}
                      {...form.getInputProps('workingHoursPerMonth')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <MultiSelect
                      label={t('profile.fields.weekdaysAvailable')}
                      placeholder={t('profile.fields.weekdaysAvailable')}
                      data={weekdayOptions}
                      value={form.values.weekdaysAvailable ?? []}
                      onChange={value =>
                        form.setFieldValue(
                          'weekdaysAvailable',
                          value as Weekday[]
                        )
                      }
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12 }}>
                    <Textarea
                      label={t('profile.fields.languages')}
                      placeholder={t('profile.placeholders.languages')}
                      minRows={2}
                      {...form.getInputProps('languages')}
                    />
                  </Grid.Col>
                </Grid>
              </div>

              <div>
                <Title order={3} mb="md" size="h4">
                  {t('profile.sections.documents')}
                </Title>
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <div>
                      <Group gap="xs">
                        <Text fw={500}>
                          {t('profile.documents.healthCertificate')}
                        </Text>
                        <Badge
                          color={
                            volunteer.healthCertificateReviewStatus ===
                            'APPROVED'
                              ? 'green'
                              : volunteer.healthCertificateReviewStatus ===
                                  'REJECTED'
                                ? 'red'
                                : 'yellow'
                          }
                          variant="light"
                        >
                          {volunteer.healthCertificateReviewStatus ===
                          'APPROVED'
                            ? t('profile.documents.approved')
                            : volunteer.healthCertificateReviewStatus ===
                                'PENDING'
                              ? t('profile.documents.pending')
                              : volunteer.healthCertificateReviewStatus ===
                                  'REJECTED'
                                ? t('profile.documents.rejected')
                                : t('profile.documents.missing')}
                        </Badge>
                      </Group>
                      {volunteer.healthCertificateUploadedAt && (
                        <Text size="sm" c="dimmed">
                          {t('profile.documents.uploadedAt', {
                            date: new Date(
                              volunteer.healthCertificateUploadedAt
                            ).toLocaleDateString(),
                          })}
                        </Text>
                      )}
                      {volunteer.healthCertificateOriginalName && (
                        <Text size="sm" c="dimmed">
                          {volunteer.healthCertificateOriginalName}
                        </Text>
                      )}
                    </div>
                    {volunteer.healthCertificateUploaded && (
                      <Group gap="xs">
                        <Button
                          component="a"
                          href={userApi.getOwnHealthCertificateUrl()}
                          target="_blank"
                          rel="noreferrer"
                          variant="light"
                          size="xs"
                          leftSection={<IconFileText size={14} />}
                        >
                          {t('profile.documents.view')}
                        </Button>
                        <Button
                          type="button"
                          variant="subtle"
                          color="red"
                          size="xs"
                          leftSection={<IconTrash size={14} />}
                          loading={deleteHealthCertificateMutation.isPending}
                          onClick={handleHealthCertificateDelete}
                        >
                          {t('profile.documents.delete')}
                        </Button>
                      </Group>
                    )}
                  </Group>
                  <FileInput
                    label={t('profile.documents.uploadLabel')}
                    placeholder={t('profile.documents.uploadPlaceholder')}
                    accept="application/pdf,image/jpeg,image/png"
                    leftSection={<IconUpload size={16} />}
                    clearable
                    disabled={uploadHealthCertificateMutation.isPending}
                    onChange={handleHealthCertificateUpload}
                  />
                  <Text size="xs" c="dimmed">
                    {t('profile.documents.helpText')}
                  </Text>
                </Stack>
              </div>

              {/* Submit Buttons */}
              <Group justify="flex-end">
                <Button variant="default" disabled={isSubmitting}>
                  {t('profile.buttons.cancel')}
                </Button>
                <Button
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t('profile.buttons.saving')
                    : t('profile.buttons.save')}
                </Button>
              </Group>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </Container>
  );
}
